import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Building2, Users, Zap, Target, UserPlus, Sparkles, Clock, Send, ClipboardList, AlertTriangle, BarChart3, TrendingUp } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';
import { TemplateEditor } from '../components/TemplateEditor';
import { ScoringConfig } from '../components/ScoringConfig';
import { ScoreSimulator } from '../components/ScoreSimulator';
import { CrossRolePatterns } from '../components/CrossRolePatterns';
import { TargetingPlaybooks } from '../components/TargetingPlaybooks';
import { toast } from 'sonner';

import {
  PipelineAlertBanner,
  AlertSettingsPanel,
  RoleSummary,
  RoleToolbar,
  RoleCard,
  AiPreviewModal,
  TemplateManagerModal,
  OutreachTab,
  PipelineTab,
  SubmissionTracker,
  PitchAnalytics,
  StartHereTab,
  API_URL,
} from '../components/pipelines';

/* ── Main Orchestrator ── */
const HighIntentFeed = ({ embedded = false }) => {
  /* ─── Pipeline (companies) state ─── */
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [qualifying, setQualifying] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState({});

  /* ─── Campaign state ─── */
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [pushingLeads, setPushingLeads] = useState(false);
  const [addingCompany, setAddingCompany] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [activeTab, setActiveTab] = useState('start_here');
  const [nonTargetCompanies, setNonTargetCompanies] = useState([]);
  const [showNonTarget, setShowNonTarget] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showStretch, setShowStretch] = useState(false);
  const [pipelineCounts, setPipelineCounts] = useState({});
  const [excludedCompanies2, setExcludedCompanies2] = useState([]);

  /* ─── Template state ─── */
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  /* ─── AI personalization state ─── */
  const [aiGenerating, setAiGenerating] = useState(null);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiTone, setAiTone] = useState('professional');
  const [aiSteps, setAiSteps] = useState(3);
  const [aiAbTesting, setAiAbTesting] = useState(false);

  /* ─── Industry mode state ─── */
  const [industryMode, setIndustryMode] = useState('preferred');
  const [industryModeLoading, setIndustryModeLoading] = useState(false);

  /* ─── Role pipeline state ─── */
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesSummary, setRolesSummary] = useState(null);
  const [filteredRolesCount, setFilteredRolesCount] = useState(0);
  const [enterpriseRoles, setEnterpriseRoles] = useState([]);
  const [showEnterpriseRoles, setShowEnterpriseRoles] = useState(false);
  const [showFilteredRoles, setShowFilteredRoles] = useState(false);
  const [detectingRoles, setDetectingRoles] = useState(null);
  const [sourcingRole, setSourcingRole] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const [roleCandidates, setRoleCandidates] = useState({});
  const [focusMyTime, setFocusMyTime] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [smartResourcing, setSmartResourcing] = useState(null);
  const [batchData, setBatchData] = useState({});
  const [pipelineAlerts, setPipelineAlerts] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [snapshotPreview, setSnapshotPreview] = useState(null);
  const [showScoringConfig, setShowScoringConfig] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [simulatorPreset, setSimulatorPreset] = useState(null);
  const [showPatterns, setShowPatterns] = useState(false);
  const [showPlaybooks, setShowPlaybooks] = useState(false);
  const [engagement, setEngagement] = useState(null);

  /* ─── Helpers ─── */
  const postWithAuth = async (url, body = {}) => fetchWithAuth(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  /* ─── Data fetchers ─── */
  const fetchData = useCallback(() => {
    setLoading(true);
    const ntParam = showNonTarget ? '&show_non_target=true' : '';
    const exParam = showExcluded ? '&show_excluded=true' : '';
    const strParam = showStretch ? '&show_stretch=true' : '';
    Promise.all([
      fetchWithAuth(`${API_URL}/api/intelligence/acquisition/high-intent/summary`),
      fetchWithAuth(`${API_URL}/api/intelligence/acquisition/high-intent?limit=500${ntParam}${exParam}${strParam}`),
      fetchWithAuth(`${API_URL}/api/intelligence/acquisition/industry-mode`),
    ]).then(([sum, data, modeData]) => {
      setSummary(sum);
      setCompanies(data.companies || data || []);
      setNonTargetCompanies(data.non_target_companies || []);
      setExcludedCompanies2(data.excluded_companies || []);
      setPipelineCounts(data.counts || {});
      if (modeData?.mode) setIndustryMode(modeData.mode);
    })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [showNonTarget, showExcluded, showStretch]);

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns`);
      setCampaigns(data.campaigns || []);
    } catch { setCampaigns([]); }
    setCampaignsLoading(false);
  }, []);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const includeFiltered = showFilteredRoles ? '&include_filtered=true' : '';
      const [rolesData, summaryData] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/intelligence/acquisition/roles?${includeFiltered}`),
        fetchWithAuth(`${API_URL}/api/intelligence/acquisition/roles/summary`),
      ]);
      setRoles(rolesData.roles || []);
      setEnterpriseRoles(rolesData.enterprise_roles || []);
      setFilteredRolesCount(rolesData.filtered_count || 0);
      setRolesSummary(summaryData);
    } catch {}
    setRolesLoading(false);
  }, [showFilteredRoles]);

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsData, settingsData] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pipeline-alerts`),
        fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pipeline-alerts/settings`),
      ]);
      setPipelineAlerts(alertsData.alerts || []);
      setAlertSettings(settingsData);
    } catch {}
  }, []);

  const fetchAllTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/sequence-templates`);
      setAllTemplates(data.templates || []);
    } catch {}
    setTemplatesLoading(false);
  }, []);

  const loadContacts = async (company) => {
    if (contacts[company]) return;
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/company/${encodeURIComponent(company)}/contacts`);
      setContacts(prev => ({ ...prev, [company]: data.contacts || [] }));
    } catch {}
  };

  const loadRoleCandidates = async (roleId, forceReload = false) => {
    if (roleCandidates[roleId] && !forceReload) return;
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/roles/${roleId}`);
      setRoleCandidates(prev => ({ ...prev, [roleId]: data.candidates || [] }));
    } catch {}
    loadRoleBatches(roleId);
  };

  const loadRoleBatches = async (roleId) => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/roles/${roleId}/batches`);
      setBatchData(prev => ({ ...prev, [roleId]: data }));
    } catch {}
  };

  /* ─── Effects ─── */
  useEffect(() => { fetchData(); fetchCampaigns(); }, [fetchData, fetchCampaigns]);
  useEffect(() => {
    if (!selectedCampaign) { setAnalytics(null); return; }
    setLoadingAnalytics(true);
    fetchWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns/${selectedCampaign.id}/analytics`)
      .then(data => { if (!data.error) setAnalytics(data); }).catch(() => {}).finally(() => setLoadingAnalytics(false));
  }, [selectedCampaign]);
  useEffect(() => {
    if (activeTab === 'roles') { fetchRoles(); fetchAlerts(); }
  }, [activeTab, fetchRoles, fetchAlerts]);
  useEffect(() => {
    if (activeTab === 'outreach') {
      fetchWithAuth(`${API_URL}/api/intelligence/acquisition/outreach-stats`)
        .then(data => { if (data.engagement) setEngagement(data.engagement); }).catch(() => {});
    }
  }, [activeTab]);

  /* ─── Action handlers ─── */
  const handleQualifyNow = async () => {
    setQualifying(true);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/qualify-now`);
      toast.success(`Qualified ${data.total_qualified} companies, ${data.new_prospects_added} new`);
      fetchData();
    } catch { toast.error('Qualification failed'); }
    setQualifying(false);
  };

  const handleEnrichNow = async () => {
    setEnriching(true);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/enrich-now?max_companies=10`);
      if (data.skipped) { toast.info(data.reason); } else { toast.success(`Enriched ${data.enriched} companies with decision-maker contacts`); fetchData(); }
    } catch { toast.error('Enrichment failed'); }
    setEnriching(false);
  };

  const handlePromote = async (company) => {
    try {
      const res = await postWithAuth(`${API_URL}/api/intelligence/acquisition/company/${encodeURIComponent(company)}/promote`);
      if (res.error) {
        toast.error(res.detail || 'Cannot promote — company is excluded');
        return;
      }
      toast.success(`${company} promoted to outreach pipeline`);
      setCompanies(prev => prev.map(c => c.company === company ? { ...c, status: 'ready_for_outreach', pipeline_stage: 'Prospect' } : c));
    } catch { toast.error('Promote failed'); }
  };

  const handleOverride = async (company) => {
    const reason = prompt(`Override reason for ${company}:`);
    if (!reason) return;
    try {
      const res = await postWithAuth(`${API_URL}/api/intelligence/acquisition/company/${encodeURIComponent(company)}/override`, {
        action: 'override', reason, note: `Manual override by operator`,
      });
      if (res.error) { toast.error(res.detail); return; }
      toast.success(`${company} overridden — now in target pipeline`);
      fetchData();
    } catch { toast.error('Override failed'); }
  };

  const handleCreateCampaign = async (name, templateId = null) => {
    try {
      const body = { name };
      if (templateId) body.template_id = templateId;
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns`, body);
      if (data.success) {
        toast.success(data.template_applied ? `Campaign "${name}" created with "${data.template_applied}" sequence` : `Campaign "${name}" created`);
        fetchCampaigns();
      } else { toast.error(data.detail || 'Failed to create campaign'); }
    } catch { toast.error('Failed to create campaign'); }
  };

  const handleActivateCampaign = async (id) => {
    try { const d = await postWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns/${id}/activate`); if (d.success) { toast.success('Campaign activated'); fetchCampaigns(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Failed to activate campaign'); }
  };
  const handlePauseCampaign = async (id) => {
    try { const d = await postWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns/${id}/pause`); if (d.success) { toast.success('Campaign paused'); fetchCampaigns(); } else toast.error(d.detail || 'Failed'); } catch { toast.error('Failed to pause campaign'); }
  };

  const handlePushLeads = async () => {
    if (!selectedCampaign) { toast.error('Select a campaign first'); return; }
    setPushingLeads(true);
    try {
      const companyNames = selectedCompanies.size > 0 ? Array.from(selectedCompanies) : null;
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns/push-leads`, { campaign_id: selectedCampaign.id, company_names: companyNames, limit: companyNames ? companyNames.length : 50 });
      toast.success(`Added ${data.leads_added} leads from ${data.companies_processed} companies`);
      setSelectedCompanies(new Set()); fetchData();
    } catch { toast.error('Failed to push leads'); }
    setPushingLeads(false);
  };

  const handleAddCompanyToCampaign = async (companyName) => {
    if (!selectedCampaign) { toast.error('Select a campaign first'); return; }
    setAddingCompany(companyName);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/campaigns/add-company`, { campaign_id: selectedCampaign.id, company_name: companyName });
      if (data.leads_added > 0) { toast.success(`Added ${data.leads_added} contacts from ${companyName}`); fetchData(); } else toast.info(`No contacts with emails found for ${companyName}`);
    } catch { toast.error('Failed to add company'); }
    setAddingCompany(null);
  };

  const handleDetectRoles = async (companyName) => {
    setDetectingRoles(companyName);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/roles/detect/${encodeURIComponent(companyName)}`);
      if (data.roles_detected > 0) { toast.success(`Detected ${data.roles_detected} roles for ${companyName} (${data.verticals_found.join(', ')})`); fetchRoles(); } else toast.info(`No specialty vertical roles found for ${companyName}`);
    } catch { toast.error('Role detection failed'); }
    setDetectingRoles(null);
  };

  const handleDetectRolesBatch = async () => {
    setDetectingRoles('_batch');
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/roles/detect-batch`);
      toast.success(`Detected ${data.total_roles_detected} roles across ${data.companies_processed} companies`);
      fetchRoles();
    } catch { toast.error('Batch detection failed'); }
    setDetectingRoles(null);
  };

  const handleSourceCandidates = async (roleId) => {
    setSourcingRole(roleId);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/roles/${roleId}/source`);
      if (data.error) toast.error(data.detail || 'Sourcing failed');
      else { toast.success(`Sourced ${data.candidates_added} candidates for "${data.role_title}"`); setRoleCandidates(prev => ({ ...prev, [roleId]: data.candidates || [] })); fetchRoles(); }
    } catch { toast.error('Candidate sourcing failed'); }
    setSourcingRole(null);
  };

  const handleRescoreAll = async () => {
    setRescoring(true);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/roles/rescore-all`);
      toast.success(`Re-scored ${data.total_candidates_rescored} candidates across ${data.roles_processed} roles`);
      setRoleCandidates({}); fetchRoles();
    } catch { toast.error('Re-scoring failed'); }
    setRescoring(false);
  };

  const handleSmartResource = async (roleId) => {
    setSmartResourcing(roleId);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/roles/${roleId}/smart-resource`);
      if (data.error) { toast.error(data.detail); return; }
      toast.success(`Smart Re-Source complete! Batch ${data.batch}: ${data.candidates_added} new candidates (avg score: ${data.comparison?.after?.avg_score})`);
      setRoleCandidates(prev => ({ ...prev, [roleId]: undefined })); loadRoleBatches(roleId); fetchRoles();
    } catch { toast.error('Smart Re-Source failed'); }
    setSmartResourcing(null);
  };

  const dismissAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('backoffice_token');
      await fetch(`${API_URL}/api/intelligence/acquisition/pipeline-alerts/${alertId}/dismiss`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      setPipelineAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch {}
  };

  const saveAlertSettings = async (updates) => {
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/pipeline-alerts/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setAlertSettings(data);
      toast.success('Alert settings saved');
    } catch { toast.error('Failed to save settings'); }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/sequence-templates/${id}`, { method: 'DELETE' });
      if (data.success) { toast.success('Template deleted'); fetchAllTemplates(); } else toast.error(data.detail || 'Cannot delete template');
    } catch { toast.error('Failed to delete'); }
  };

  const handleAiPersonalize = async (companyName) => {
    setAiGenerating(companyName);
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/ai/personalize`, { company_name: companyName, num_steps: aiSteps, tone: aiTone, ab_testing: aiAbTesting });
      if (data.error) toast.error(data.detail || 'AI generation failed');
      else { setAiPreview({ ...data, company_name: companyName }); toast.success(`AI generated ${data.steps_count}-step ${data.ab_testing ? 'A/B ' : ''}sequence for ${companyName}`); }
    } catch { toast.error('AI personalization failed'); }
    setAiGenerating(null);
  };

  const handleAiSaveAsTemplate = async () => {
    if (!aiPreview) return;
    try {
      const data = await postWithAuth(`${API_URL}/api/intelligence/acquisition/ai/personalize-and-save`, { company_name: aiPreview.company_name, num_steps: aiSteps, tone: aiTone, ab_testing: aiAbTesting });
      if (data.success) { toast.success(`Saved as template: ${data.template.name}`); setAiPreview(null); fetchAllTemplates(); } else toast.error(data.detail || 'Failed to save');
    } catch { toast.error('Failed to save template'); }
  };

  const toggleCompanySelection = (company) => {
    setSelectedCompanies(prev => { const next = new Set(prev); if (next.has(company)) next.delete(company); else next.add(company); return next; });
  };

  /* ─── Computed values ─── */
  const sortedRoles = (() => {
    let filtered = roles;
    if (focusMyTime) {
      // Exclude low viability, defense/clearance, excluded companies
      filtered = roles.filter(r => {
        const viab = r.viability || {};
        if (viab.excluded) return false;
        if (viab.below_threshold) return false;
        if (viab.tags?.includes('clearance_required')) return false;
        return true;
      });
      const scored = filtered.map(r => {
        const pq = r.pipeline_quality || {};
        const viab = r.viability?.score || 50;
        const composite = (pq.avg_score || 0) * 2 + (pq.high_fit || 0) * 15 + (pq.medium_fit || 0) * 5 + (r.candidates_count || 0) * 0.5 + viab * 0.5;
        return { ...r, _composite: composite };
      });
      scored.sort((a, b) => b._composite - a._composite);
      return scored;
    }
    return filtered;
  })();

  const outreachCount = companies.filter(c => c.outreach_status === 'added_to_campaign').length;

  /* ─── Render ─── */
  return (
    <IntelligenceLayout title="Acquisition Pipeline" embedded={embedded}>
      {/* Page Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-[#0A1F44] to-[#1a3a6b] rounded-xl flex items-center justify-center">
              <Target className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="high-intent-title">Acquisition Pipeline</h1>
              <p className="text-sm text-gray-500">Auto-qualified prospects with enriched contacts & automated outreach</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleQualifyNow} disabled={qualifying}
              className="px-4 py-2 bg-[#0A1F44] text-white text-xs font-medium rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1.5"
              data-testid="qualify-now-btn">
              {qualifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Re-Qualify
            </button>
            <button onClick={handleEnrichNow} disabled={enriching}
              className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-semibold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1.5"
              data-testid="enrich-now-btn">
              {enriching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Enrich Contacts
            </button>
          </div>
        </div>
        <div className="flex gap-1 mt-4" data-testid="pipeline-tabs">
          <button onClick={() => setActiveTab('start_here')}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors ${activeTab === 'start_here' ? 'bg-white text-[#0A1F44] border border-b-0 border-gray-200' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            data-testid="tab-start-here">
            <Target className="h-3.5 w-3.5 inline mr-1.5 text-[#D4AF37]" />Start Here
          </button>
          <button onClick={() => setActiveTab('pipeline')}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors ${activeTab === 'pipeline' ? 'bg-white text-[#0A1F44] border border-b-0 border-gray-200' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            data-testid="tab-pipeline">
            <Target className="h-3.5 w-3.5 inline mr-1.5" />Pipeline ({companies.length})
          </button>
          <button onClick={() => setActiveTab('outreach')}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors ${activeTab === 'outreach' ? 'bg-white text-[#0A1F44] border border-b-0 border-gray-200' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            data-testid="tab-outreach">
            <Send className="h-3.5 w-3.5 inline mr-1.5" />Outreach Campaigns {outreachCount > 0 && <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] rounded-full">{outreachCount}</span>}
          </button>
          <button onClick={() => setActiveTab('roles')}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors ${activeTab === 'roles' ? 'bg-white text-[#0A1F44] border border-b-0 border-gray-200' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            data-testid="tab-roles">
            <Users className="h-3.5 w-3.5 inline mr-1.5" />Candidate Pipelines {rolesSummary?.total_candidates > 0 && <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded-full">{rolesSummary.total_candidates}</span>}
          </button>
          <button onClick={() => setActiveTab('submissions')}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors ${activeTab === 'submissions' ? 'bg-white text-[#0A1F44] border border-b-0 border-gray-200' : 'text-gray-500 hover:text-[#0A1F44]'}`}
            data-testid="tab-submissions">
            <ClipboardList className="h-3.5 w-3.5 inline mr-1.5" />Submissions
          </button>
        </div>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading pipeline...</div>
        ) : (
          <>
            {summary && (
              <div className="space-y-4" data-testid="pipeline-metrics">
                {/* Priority Tiers — company classification */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#0A1F44]" />
                    <span className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-widest">Priority Tiers</span>
                    <span className="text-[10px] text-gray-400">Company classification: size, industry, confidence, exclusion rules</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <MetricCard label="Best Immediate" value={pipelineCounts.high_fit ?? summary.high_fit_count ?? 0} icon={Target} subtext="Mid-market + verified data" />
                    <MetricCard label="Broader Qualified" value={pipelineCounts.moderate_fit ?? summary.moderate_fit_count ?? 0} icon={TrendingUp} subtext="Qualified, lower priority" />
                    <MetricCard label="Needs Enrichment" value={pipelineCounts.stretch_risk ?? summary.stretch_risk_count ?? 0} icon={AlertTriangle} subtext="Missing size / industry data" />
                    <MetricCard label="Strategic Enterprise" value={pipelineCounts.non_target ?? summary.non_target_count ?? 0} icon={Building2} subtext="Large enterprise, hidden by default" />
                    <MetricCard label="Excluded" value={pipelineCounts.excluded ?? summary.excluded_count ?? 0} icon={AlertTriangle} subtext="Staffing, retail, defense" />
                  </div>
                </div>
                {/* Activity & Pipeline — hiring signals */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                    <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">Pipeline Activity</span>
                    <span className="text-[10px] text-gray-400">Hiring signals: posting volume, surge, hard-to-fill roles</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MetricCard label="Total Qualified" value={summary.total_qualified ?? 0} icon={Zap} subtext="Passed fit + score gate" />
                    <MetricCard label="High Activity" value={summary.high_priority ?? 0} icon={BarChart3} subtext="Activity score 12+" />
                    <MetricCard label="Moderate Activity" value={summary.medium_priority ?? 0} icon={BarChart3} subtext="Activity score 9-11" />
                    <MetricCard label="Enriched" value={summary.enriched ?? 0} icon={Users} subtext="Contacts discovered" />
                  </div>
                </div>
              </div>
            )}

            {/* Industry Mode Control */}
            {summary && (
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-2.5" data-testid="industry-mode-control">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-widest">Industry Mode</span>
                  </div>
                  <span className="text-[10px] text-gray-400">
                    {industryMode === 'strict' && 'Only ICP industries qualify without employee data'}
                    {industryMode === 'preferred' && 'ICP industries boosted + high-scoring non-ICP companies (9+) qualify'}
                    {industryMode === 'open' && 'All industries qualify with any industry signal'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {['strict', 'preferred', 'open'].map(mode => (
                    <button
                      key={mode}
                      disabled={industryModeLoading}
                      onClick={async () => {
                        if (mode === industryMode) return;
                        setIndustryModeLoading(true);
                        try {
                          await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/industry-mode`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ mode, run_backfill: true }),
                          });
                          setIndustryMode(mode);
                          fetchData();
                          toast.success(`Industry mode set to ${mode}`);
                        } catch { toast.error('Failed to update mode'); }
                        setIndustryModeLoading(false);
                      }}
                      className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all ${
                        industryMode === mode
                          ? 'bg-[#0A1F44] text-[#D4AF37]'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      } ${industryModeLoading ? 'opacity-50 cursor-wait' : ''}`}
                      data-testid={`industry-mode-${mode}`}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ─── Outreach Tab ─── */}
            {activeTab === 'outreach' && (
              <OutreachTab
                engagement={engagement}
                campaigns={campaigns} campaignsLoading={campaignsLoading} fetchCampaigns={fetchCampaigns}
                onCreateCampaign={handleCreateCampaign}
                selectedCampaign={selectedCampaign} setSelectedCampaign={setSelectedCampaign}
                onActivateCampaign={handleActivateCampaign} onPauseCampaign={handlePauseCampaign}
                analytics={analytics} loadingAnalytics={loadingAnalytics}
                onManageTemplates={() => { setTemplateManagerOpen(true); fetchAllTemplates(); }}
                selectedCompanies={selectedCompanies}
                pushingLeads={pushingLeads} onPushLeads={handlePushLeads}
                companies={companies}
              />
            )}

            {/* ─── Candidate Pipelines Tab ─── */}
            {activeTab === 'roles' && (
              <>
                <PipelineAlertBanner
                  alerts={pipelineAlerts}
                  onDismiss={dismissAlert}
                  onViewCandidates={(roleId) => { setExpandedRole(roleId); }}
                />

                {showAlertSettings && (
                  <AlertSettingsPanel settings={alertSettings} onSave={saveAlertSettings} onClose={() => setShowAlertSettings(false)} />
                )}

                {showScoringConfig && (
                  <div className="mb-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="scoring-config-section">
                    <ScoringConfig onClose={() => setShowScoringConfig(false)} onSaved={() => {}} />
                  </div>
                )}

                {showPatterns && (
                  <div className="mb-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="cross-role-patterns-panel">
                    <CrossRolePatterns onApplyPattern={(preset) => { setSimulatorPreset(preset); setShowPatterns(false); setShowSimulator(true); }} />
                  </div>
                )}

                {showPlaybooks && (
                  <div className="mb-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="playbooks-panel">
                    <TargetingPlaybooks
                      onApplyToSimulator={(preset) => { setSimulatorPreset(preset); setShowPlaybooks(false); setShowSimulator(true); }}
                      onApplyToRole={expandedRole ? async (pb) => {
                        const tkn = localStorage.getItem('backoffice_token');
                        try {
                          const res = await fetch(`${API_URL}/api/intelligence/acquisition/roles/${expandedRole}/apply-playbook`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` },
                            body: JSON.stringify({ playbook_id: pb.playbook_id }),
                          });
                          const d = await res.json();
                          if (d.ok) { toast.success(`Applied: ${d.label}`); fetchRoles(); loadRoleCandidates(expandedRole); }
                          else toast.error(d.detail || 'Failed');
                        } catch { toast.error('Failed to apply playbook'); }
                      } : null}
                    />
                  </div>
                )}

                <RoleSummary rolesSummary={rolesSummary} />

                <RoleToolbar
                  rolesCount={roles.length}
                  focusMyTime={focusMyTime} onToggleFocus={() => setFocusMyTime(f => !f)}
                  rescoring={rescoring} onRescoreAll={handleRescoreAll}
                  detectingRoles={detectingRoles} onDetectBatch={handleDetectRolesBatch}
                  rolesLoading={rolesLoading} onRefresh={fetchRoles}
                  showAlertSettings={showAlertSettings} onToggleAlertSettings={() => setShowAlertSettings(s => !s)}
                  showScoringConfig={showScoringConfig} onToggleScoringConfig={() => setShowScoringConfig(s => !s)}
                  onOpenSimulator={() => setShowSimulator(true)}
                  showPatterns={showPatterns} onTogglePatterns={() => setShowPatterns(s => !s)}
                  showPlaybooks={showPlaybooks} onTogglePlaybooks={() => setShowPlaybooks(s => !s)}
                />

                {rolesLoading ? (
                  <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading roles...</div>
                ) : sortedRoles.length === 0 ? (
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                      <Users className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 mb-2">{(filteredRolesCount + enterpriseRoles.length) > 0 ? 'All roles filtered by viability rules' : 'No role pipelines yet'}</p>
                      <p className="text-xs text-gray-400">{(filteredRolesCount + enterpriseRoles.length) > 0 ? `${enterpriseRoles.length} enterprise + ${filteredRolesCount} filtered roles in low-priority queues` : 'Click "Scan All Companies" to detect active roles in your specialty verticals'}</p>
                    </div>
                    {enterpriseRoles.length > 0 && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="enterprise-roles-section-empty">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-orange-400" />
                            <span className="text-xs font-medium text-gray-600">{enterpriseRoles.length} Enterprise (Low Priority)</span>
                            <span className="text-[10px] text-gray-400">(Amazon, Google, Deloitte, etc.)</span>
                          </div>
                          <button onClick={() => setShowEnterpriseRoles(s => !s)}
                            className="px-3 py-1 text-[10px] font-medium text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
                            data-testid="toggle-enterprise-roles-btn">
                            {showEnterpriseRoles ? 'Hide' : 'Show'}
                          </button>
                        </div>
                        {showEnterpriseRoles && enterpriseRoles.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {enterpriseRoles.map((role, ri) => (
                              <RoleCard key={role.role_id} role={role} roleIndex={`ent-${ri}`}
                                isExpanded={expandedRole === role.role_id}
                                onToggle={() => { setExpandedRole(expandedRole === role.role_id ? null : role.role_id); if (expandedRole !== role.role_id) loadRoleCandidates(role.role_id); }}
                                candidates={roleCandidates[role.role_id] || []} batchData={batchData[role.role_id]}
                                isFocusTop={false} focusRank={999} sourcingRole={sourcingRole}
                                onSource={handleSourceCandidates} smartResourcing={smartResourcing}
                                onSmartResource={handleSmartResource} snapshotPreview={snapshotPreview}
                                onSnapshotPreviewToggle={(id) => setSnapshotPreview(snapshotPreview === id ? null : id)}
                                onCandidateNoteAdded={() => loadRoleCandidates(role.role_id, true)}
                                onCandidateSubmissionToggled={(ck, val) => { setRoleCandidates(prev => ({ ...prev, [role.role_id]: (prev[role.role_id] || []).map(x => x.candidate_key === ck ? { ...x, in_submission: val } : x) })); }}
                                onCandidateStageChanged={(ck, stage) => { setRoleCandidates(prev => ({ ...prev, [role.role_id]: (prev[role.role_id] || []).map(x => x.candidate_key === ck ? { ...x, stage } : x) })); }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {filteredRolesCount > 0 && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="filtered-roles-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-gray-400" />
                            <span className="text-xs font-medium text-gray-600">{filteredRolesCount} Filtered / Excluded</span>
                            <span className="text-[10px] text-gray-400">(defense contractors, clearance-required)</span>
                          </div>
                          <button onClick={() => setShowFilteredRoles(s => !s)}
                            className="px-3 py-1 text-[10px] font-medium text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
                            data-testid="toggle-filtered-roles-btn">
                            {showFilteredRoles ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {focusMyTime && sortedRoles.some(r => (r._composite || 0) > 0) && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg mb-2" data-testid="focus-banner">
                        <Zap className="h-4 w-4 text-[#D4AF37]" />
                        <span className="text-xs font-bold text-[#0A1F44]">Focus My Time</span>
                        <span className="text-[10px] text-gray-500">Top opportunities ranked by pipeline strength. Focus on the first roles for highest placement potential.</span>
                      </div>
                    )}
                    {focusMyTime && !sortedRoles.some(r => (r._composite || 0) > 0) && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg mb-2" data-testid="no-focus-banner">
                        <span className="text-xs font-medium text-red-700">No strong pipelines found. Source more candidates or refine role criteria to improve match quality.</span>
                      </div>
                    )}
                    {sortedRoles.map((role, ri) => (
                      <RoleCard
                        key={role.role_id}
                        role={role}
                        roleIndex={ri}
                        isExpanded={expandedRole === role.role_id}
                        onToggle={() => { setExpandedRole(expandedRole === role.role_id ? null : role.role_id); if (expandedRole !== role.role_id) loadRoleCandidates(role.role_id); }}
                        candidates={roleCandidates[role.role_id] || []}
                        batchData={batchData[role.role_id]}
                        isFocusTop={focusMyTime && ri < 3 && (role._composite || 0) > 0}
                        focusRank={ri}
                        sourcingRole={sourcingRole}
                        onSource={handleSourceCandidates}
                        smartResourcing={smartResourcing}
                        onSmartResource={handleSmartResource}
                        snapshotPreview={snapshotPreview}
                        onSnapshotPreviewToggle={(id) => setSnapshotPreview(snapshotPreview === id ? null : id)}
                        onCandidateNoteAdded={() => loadRoleCandidates(role.role_id, true)}
                        onCandidateSubmissionToggled={(ck, val) => {
                          setRoleCandidates(prev => {
                            const updated = (prev[role.role_id] || []).map(x => x.candidate_key === ck ? { ...x, in_submission: val } : x);
                            return { ...prev, [role.role_id]: updated };
                          });
                        }}
                        onCandidateStageChanged={(ck, stage) => {
                          setRoleCandidates(prev => {
                            const updated = (prev[role.role_id] || []).map(x => x.candidate_key === ck ? { ...x, stage } : x);
                            return { ...prev, [role.role_id]: updated };
                          });
                        }}
                      />
                    ))}

                    {/* Enterprise (Low Priority) Section */}
                    {enterpriseRoles.length > 0 && (
                      <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg" data-testid="enterprise-roles-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-orange-400" />
                            <span className="text-xs font-medium text-orange-700">
                              {enterpriseRoles.length} Enterprise (Low Priority)
                            </span>
                            <span className="text-[10px] text-orange-400">(large companies with internal recruiting teams)</span>
                          </div>
                          <button onClick={() => setShowEnterpriseRoles(s => !s)}
                            className="px-3 py-1 text-[10px] font-medium text-orange-600 border border-orange-300 rounded hover:bg-orange-100"
                            data-testid="toggle-enterprise-roles-btn">
                            {showEnterpriseRoles ? 'Hide' : 'Show'}
                          </button>
                        </div>
                        {showEnterpriseRoles && (
                          <div className="mt-2 space-y-2">
                            {enterpriseRoles.map((role, ri) => (
                              <RoleCard key={role.role_id} role={role} roleIndex={`ent-${ri}`}
                                isExpanded={expandedRole === role.role_id}
                                onToggle={() => { setExpandedRole(expandedRole === role.role_id ? null : role.role_id); if (expandedRole !== role.role_id) loadRoleCandidates(role.role_id); }}
                                candidates={roleCandidates[role.role_id] || []} batchData={batchData[role.role_id]}
                                isFocusTop={false} focusRank={999} sourcingRole={sourcingRole}
                                onSource={handleSourceCandidates} smartResourcing={smartResourcing}
                                onSmartResource={handleSmartResource} snapshotPreview={snapshotPreview}
                                onSnapshotPreviewToggle={(id) => setSnapshotPreview(snapshotPreview === id ? null : id)}
                                onCandidateNoteAdded={() => loadRoleCandidates(role.role_id, true)}
                                onCandidateSubmissionToggled={(ck, val) => { setRoleCandidates(prev => ({ ...prev, [role.role_id]: (prev[role.role_id] || []).map(x => x.candidate_key === ck ? { ...x, in_submission: val } : x) })); }}
                                onCandidateStageChanged={(ck, stage) => { setRoleCandidates(prev => ({ ...prev, [role.role_id]: (prev[role.role_id] || []).map(x => x.candidate_key === ck ? { ...x, stage } : x) })); }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Filtered / Excluded Roles Section */}
                    {filteredRolesCount > 0 && (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="filtered-roles-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-gray-400" />
                            <span className="text-xs font-medium text-gray-600">
                              {filteredRolesCount} Filtered / Excluded
                            </span>
                            <span className="text-[10px] text-gray-400">(defense contractors, clearance-required)</span>
                          </div>
                          <button onClick={() => { setShowFilteredRoles(s => !s); }}
                            className="px-3 py-1 text-[10px] font-medium text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
                            data-testid="toggle-filtered-roles-btn">
                            {showFilteredRoles ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ─── Start Here Tab ─── */}
            {activeTab === 'start_here' && <StartHereTab />}

            {/* ─── Pipeline Tab ─── */}
            {activeTab === 'pipeline' && (
              <PipelineTab
                companies={companies}
                nonTargetCompanies={nonTargetCompanies}
                excludedCompanies={excludedCompanies2}
                counts={pipelineCounts}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                filterPriority={filterPriority} setFilterPriority={setFilterPriority}
                selectedCompanies={selectedCompanies} setSelectedCompanies={setSelectedCompanies} toggleCompanySelection={toggleCompanySelection}
                expandedRow={expandedRow} setExpandedRow={setExpandedRow}
                contacts={contacts} loadContacts={loadContacts}
                selectedCampaign={selectedCampaign}
                pushingLeads={pushingLeads} onPushLeads={handlePushLeads}
                addingCompany={addingCompany} onAddCompanyToCampaign={handleAddCompanyToCampaign}
                onPromote={handlePromote}
                onOverride={handleOverride}
                detectingRoles={detectingRoles} onDetectRoles={handleDetectRoles}
                aiGenerating={aiGenerating} onAiPersonalize={handleAiPersonalize}
                setActiveTab={setActiveTab}
                showNonTarget={showNonTarget} setShowNonTarget={(fn) => { setShowNonTarget(fn); }}
                showExcluded={showExcluded} setShowExcluded={(fn) => { setShowExcluded(fn); }}
                showStretch={showStretch} setShowStretch={(fn) => { setShowStretch(fn); }}
              />
            )}

            {/* ─── Submissions Tab ─── */}
            {activeTab === 'submissions' && (
              <div className="space-y-6">
                <SubmissionTracker />
                <div>
                  <h3 className="text-sm font-bold text-[#0A1F44] mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-[#D4AF37]" /> Pitch Email Analytics
                  </h3>
                  <PitchAnalytics />
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* ─── Modals ─── */}
      <AiPreviewModal
        aiPreview={aiPreview} aiGenerating={aiGenerating}
        aiTone={aiTone} setAiTone={setAiTone}
        aiSteps={aiSteps} setAiSteps={setAiSteps}
        aiAbTesting={aiAbTesting} setAiAbTesting={setAiAbTesting}
        onRegenerate={handleAiPersonalize}
        onSaveAsTemplate={handleAiSaveAsTemplate}
        onClose={() => setAiPreview(null)}
      />

      <TemplateManagerModal
        open={templateManagerOpen}
        templates={allTemplates}
        loading={templatesLoading}
        onClose={() => setTemplateManagerOpen(false)}
        onCreateNew={() => { setEditingTemplateId(null); setShowTemplateEditor(true); setTemplateManagerOpen(false); }}
        onEdit={(id) => { setEditingTemplateId(id); setShowTemplateEditor(true); setTemplateManagerOpen(false); }}
        onDelete={handleDeleteTemplate}
      />

      {showTemplateEditor && (
        <TemplateEditor
          templateId={editingTemplateId}
          onClose={() => { setShowTemplateEditor(false); setEditingTemplateId(null); }}
          onSaved={() => { fetchAllTemplates(); fetchCampaigns(); }}
        />
      )}
      {showSimulator && (
        <ScoreSimulator
          onClose={() => { setShowSimulator(false); setSimulatorPreset(null); }}
          existingRole={expandedRole ? roles.find(r => r.role_id === expandedRole) : null}
          onTargetingApplied={() => { fetchRoles(); if (expandedRole) loadRoleCandidates(expandedRole); }}
          preset={simulatorPreset}
        />
      )}
    </IntelligenceLayout>
  );
};

export default HighIntentFeed;
